import dayjs from '@didaskill/services/dayjs';

export enum PARSE_DURATION_BASE_UNITS {
  SECOND = 'SECOND',
  MINUTE = 'MINUTE',
  DAYS = 'DAYS',
}

export enum PARSE_DURATION_OUTPUT_UNITS {
  DAYS = 'days',
  HOURS = 'hours',
  MINUTES = 'minutes',
  SECONDS = 'seconds',
}

export const DAY_IN_HOURS = 24;
export const HOUR_IN_MINUTES = 60;
export const MINUTE_IN_SECONDS = 60;

export const DAY_IN_SECONDS = DAY_IN_HOURS * HOUR_IN_MINUTES * MINUTE_IN_SECONDS;
export const HOUR_IN_SECONDS = HOUR_IN_MINUTES * MINUTE_IN_SECONDS;
export const DAY_IN_MINUTES = DAY_IN_HOURS * HOUR_IN_MINUTES;

type ParseDurationResult = {
  hours: number;
  minutes: number;
  days: number;
  seconds: number;
};

type ParseDurationOptions = {
  outputUnits?: PARSE_DURATION_OUTPUT_UNITS[];
};

/**
 * Parse a duration.
 *
 * @param value - Duration.
 * @param unit - Unit of duration param (parseDuration.BASE_UNITS enum).
 * @param options - Optional output units to include in the parsed result.
 *                  Omitted larger units are carried into the next selected unit.
 * @returns Parsed duration.
 */
export const parseDuration = (
  value: number,
  unit: PARSE_DURATION_BASE_UNITS,
  options?: ParseDurationOptions,
) => {
  let valueForComputation = value;

  if (unit === PARSE_DURATION_BASE_UNITS.MINUTE) {
    valueForComputation = value * MINUTE_IN_SECONDS;
  } else if (unit === PARSE_DURATION_BASE_UNITS.DAYS) {
    valueForComputation = value * DAY_IN_SECONDS;
  }

  const allOutputUnits = [
    PARSE_DURATION_OUTPUT_UNITS.DAYS,
    PARSE_DURATION_OUTPUT_UNITS.HOURS,
    PARSE_DURATION_OUTPUT_UNITS.MINUTES,
    PARSE_DURATION_OUTPUT_UNITS.SECONDS,
  ];
  const requestedUnits = options?.outputUnits?.length ? options.outputUnits : allOutputUnits;

  const computationUnits: Array<{ key: PARSE_DURATION_OUTPUT_UNITS; durationInSeconds: number }> = [
    { key: PARSE_DURATION_OUTPUT_UNITS.DAYS, durationInSeconds: DAY_IN_SECONDS },
    { key: PARSE_DURATION_OUTPUT_UNITS.HOURS, durationInSeconds: HOUR_IN_SECONDS },
    { key: PARSE_DURATION_OUTPUT_UNITS.MINUTES, durationInSeconds: MINUTE_IN_SECONDS },
    { key: PARSE_DURATION_OUTPUT_UNITS.SECONDS, durationInSeconds: 1 },
  ].filter(({ key }) => requestedUnits.includes(key));

  const parsedDuration: ParseDurationResult = {
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  };

  let remainingSeconds = valueForComputation;
  computationUnits.forEach(({ key, durationInSeconds }) => {
    const unitValue = Math.floor(remainingSeconds / durationInSeconds);
    parsedDuration[key] = unitValue;
    remainingSeconds -= unitValue * durationInSeconds;
  });

  return parsedDuration;
};

parseDuration.BASE_UNITS = PARSE_DURATION_BASE_UNITS;
parseDuration.OUTPUT_UNITS = PARSE_DURATION_OUTPUT_UNITS;

/**
 * Format time for audio display.
 *
 * @param timeInSeconds - Time in seconds to format.
 * @returns Formatted time.
 */
export const formatTimeForAudio = (timeInSeconds: number) => {
  const totalSeconds = Math.floor(timeInSeconds);
  const hours = Math.floor(totalSeconds / HOUR_IN_SECONDS);
  const minutes = Math.floor((totalSeconds % HOUR_IN_SECONDS) / MINUTE_IN_SECONDS);
  const remainingSeconds = totalSeconds % MINUTE_IN_SECONDS;

  return [
    hours ? `${hours}` : null,
    hours ? minutes.toString().padStart(2, '0') : minutes.toString().padStart(1, '0'),
    remainingSeconds.toString().padStart(2, '0'),
  ]
    .filter(Boolean)
    .join(':');
};

/**
 * Returns the time unit by subtracting the current date from the date passed as a parameter.
 */
export const getUnit = (date: Date): { unit: 'second' | 'minute' | 'hour' | 'day' } | null => {
  const diffInSeconds = dayjs(date).diff(dayjs(), 'seconds');

  if (diffInSeconds < MINUTE_IN_SECONDS) {
    return { unit: 'second' };
  }

  if (diffInSeconds < HOUR_IN_SECONDS) {
    return { unit: 'minute' };
  }

  if (diffInSeconds < DAY_IN_SECONDS) {
    return { unit: 'hour' };
  }

  return { unit: 'day' };
};
